import type { ScoreBand } from '@/shared/api/resources';

/** O teto da escala do score: o mesmo das faixas de CRD-003, de 0 a 1000. */
export const ESCALA_MAXIMA = 1000;

/** O score cabe na escala. Fora dela o motor manda para analise manual. */
export function naEscala(score: number): boolean {
  return score >= 0 && score <= ESCALA_MAXIMA;
}

/**
 * A faixa em que o score cai, pela mesma conta do motor (`ClassFor`): inclusiva nas duas
 * pontas. Score fora das faixas nao tem classe, nem aproximada.
 */
export function faixaDoScore(score: number, faixas: ScoreBand[]): ScoreBand | null {
  return faixas.find((f) => score >= f.min && score <= f.max) ?? null;
}

/** As faixas do menor score para o maior, a ordem do medidor e da legenda. */
export function faixasEmOrdem(faixas: ScoreBand[]): ScoreBand[] {
  return [...faixas].sort((a, b) => a.min - b.min);
}

/** Um pedaco do arco: uma faixa, ou um vazio entre as faixas (`faixa` nula). */
export interface TrechoDoMedidor {
  inicio: number;
  fim: number;
  faixa: ScoreBand | null;
}

/**
 * Os trechos do arco de 0 a 1000, proporcionais as faixas: mudou a regra, muda o desenho.
 * O que nenhuma faixa cobre, como o 0 a 2 do padrao, vira um trecho vazio.
 */
export function trechosDoMedidor(faixas: ScoreBand[]): TrechoDoMedidor[] {
  const trechos: TrechoDoMedidor[] = [];
  let posicao = 0;
  for (const faixa of faixasEmOrdem(faixas)) {
    if (faixa.min > posicao) {
      trechos.push({ inicio: posicao, fim: faixa.min, faixa: null });
    }
    // A faixa vai ate o comeco da seguinte: 3 a 50 e 51 a 100 ficam encostadas.
    const fim = Math.min(faixa.max + 1, ESCALA_MAXIMA);
    trechos.push({ inicio: faixa.min, fim, faixa });
    posicao = fim;
  }
  if (posicao < ESCALA_MAXIMA) {
    trechos.push({ inicio: posicao, fim: ESCALA_MAXIMA, faixa: null });
  }
  return trechos;
}

export interface Ponto {
  x: number;
  y: number;
}

/** O ponto do valor no meio circulo: 0 na esquerda, 500 no alto, 1000 na direita. */
export function pontoDoArco(valor: number, centro: Ponto, raio: number): Ponto {
  const angulo = Math.PI * (1 - valor / ESCALA_MAXIMA);
  return {
    x: centro.x + raio * Math.cos(angulo),
    y: centro.y - raio * Math.sin(angulo),
  };
}

/** O caminho SVG do arco entre dois valores da escala. */
export function caminhoDoArco(
  inicio: number,
  fim: number,
  centro: Ponto,
  raio: number,
): string {
  const a = pontoDoArco(inicio, centro, raio);
  const b = pontoDoArco(fim, centro, raio);
  return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${raio} ${raio} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
}
